import { DateTime } from "luxon";
import { z } from "zod/v4-mini";

import { ApiDataSourceSpec, apiDataSource } from "@/io/datasource/ApiDatasource.ts";
import { BGMData, bgmDataSchema } from "@/models/BGMLogbook.ts";
import { activitySchema } from "@/models/diabetes/ActivityModel.ts";
import { dailyFileReportSchema } from "@/models/diabetes/DailyFileReport.ts";
import { foodSchema } from "@/models/diabetes/FoodModel.ts";
import { glycemiaSchema } from "@/models/diabetes/GlycemiaModel.ts";
import { reportSchema } from "@/models/diabetes/ReportModel.ts";
import {
  BGMGlycemiaParameters,
  CGMGlycemiaParameters,
  EditBGMGlycemiaParameters,
  EditCGMGlycemiaParameters,
  bgmGlycemiaParametersSchema,
  cgmGlycemiaParametersSchema,
} from "@/models/DiabetesDataModel.ts";
import { GlycemiaIndicatorsResponse, glycemiaIndicatorsSchema } from "@/models/GlycemiaStatsModel.ts";
import {
  InsulinContinuousIndicators,
  InsulinIndicatorsResponse,
  insulinContinuousIndicatorsSchema,
  insulinIndicatorsSchema,
} from "@/models/InsulinStatsModel.ts";
import { MedicalDevice, medicalDeviceSchema } from "@/models/MedicalDeviceModel.ts";
import { PumpModeIndicators, pumpModeIndicatorsSchema } from "@/models/PumpModeStatsModel.ts";
import { DateUtils } from "@/utils/dateUtils.ts";
import { cz } from "@/utils/zodUtils.ts";

export class DiabetesRepository {
  constructor(private readonly datasource: ApiDataSourceSpec = apiDataSource) {}

  getMedicalDevices = (patientId: string): Promise<MedicalDevice[]> => {
    return this.datasource.get(`/practitioner/devices/${patientId}/`, cz.forcedArray(medicalDeviceSchema));
  };

  getBGMLogbook = (patientId: string, from: DateTime, to: DateTime): Promise<BGMData[]> =>
    this.datasource.get(`/bgm-logbook/${patientId}`, cz.forcedArray(bgmDataSchema), {
      params: {
        from: from.startOf("day").toISO(),
        to: to.endOf("day").toISO(),
      },
    });

  getGlycemia = (patientId: string, from: DateTime, to: DateTime) => {
    return this.datasource.get(`/practitioner/glycemia/${patientId}/`, cz.forcedArray(glycemiaSchema), {
      params: {
        start: from.toISO(),
        end: to.toISO(),
      },
    });
  };

  getFood = (patientId: string, from: DateTime, to: DateTime) => {
    return this.datasource.get(`/practitioner/food/${patientId}/`, cz.forcedArray(foodSchema), {
      params: {
        start: from.toISO(),
        end: to.toISO(),
      },
    });
  };

  getActivity = (patientId: string, from: DateTime, to: DateTime) => {
    return this.datasource.get(`/practitioner/activity/${patientId}/`, cz.forcedArray(activitySchema), {
      params: {
        start: from.toISO(),
        end: to.toISO(),
      },
    });
  };

  getReports = (patientId: string, from: DateTime, to: DateTime) => {
    return this.datasource.get(`/practitioner/reports/${patientId}/`, cz.forcedArray(reportSchema), {
      params: {
        start: from.toISO(),
        end: to.toISO(),
      },
    });
  };

  getCGMParameters = (patientId: string): Promise<CGMGlycemiaParameters> => {
    return this.datasource.get(`/practitioner/parameters/cgm/${patientId}/`, cgmGlycemiaParametersSchema);
  };

  editCGMParameters = ({ id, ...parameters }: EditCGMGlycemiaParameters): Promise<CGMGlycemiaParameters> => {
    return this.datasource.patch(`/practitioner/parameters/cgm/${id}/`, cgmGlycemiaParametersSchema, parameters);
  };

  getBGMParameters = (patientId: string): Promise<BGMGlycemiaParameters> => {
    return this.datasource.get(`/practitioner/parameters/bgm/${patientId}/`, bgmGlycemiaParametersSchema);
  };

  editBGMParameters = ({ id, ...parameters }: EditBGMGlycemiaParameters): Promise<BGMGlycemiaParameters> => {
    return this.datasource.patch(`/practitioner/parameters/bgm/${id}/`, bgmGlycemiaParametersSchema, parameters);
  };

  getGlycemiaIndicators = (
    patientId: string,
    from: DateTime<true>,
    to: DateTime<true>,
    glycemiaMeasurementMethod: string,
  ): Promise<GlycemiaIndicatorsResponse> => {
    return this.datasource.get(`/practitioner/indicators/glycemia/${patientId}/`, glycemiaIndicatorsSchema, {
      params: {
        start_date: from.startOf("day").toISO(),
        end_date: to.plus({ day: 1 }).startOf("day").toISO(),
        glycemia_measurement_method: glycemiaMeasurementMethod,
        timezone_utc_offset: DateUtils.tzOffset(from) / 60,
      },
    });
  };

  getInsulinDiscreteIndicators = (
    patientId: string,
    from: DateTime<true>,
    to: DateTime<true>,
  ): Promise<InsulinIndicatorsResponse> => {
    return this.datasource.get(`/practitioner/indicators/insulin/${patientId}/`, insulinIndicatorsSchema, {
      params: {
        start_date: from.startOf("day").toISO(),
        end_date: to.plus({ day: 1 }).startOf("day").toISO(),
        insulin_injection_method: "pen",
        timezone_utc_offset: DateUtils.tzOffset(from) / 60,
      },
    });
  };

  getInsulinContinuousIndicators = (
    patientId: string,
    from: DateTime<true>,
    to: DateTime<true>,
  ): Promise<InsulinContinuousIndicators> => {
    return this.datasource.get(`/practitioner/indicators/insulin/${patientId}/`, insulinContinuousIndicatorsSchema, {
      params: {
        start_date: from.startOf("day").toISO(),
        end_date: to.plus({ day: 1 }).startOf("day").toISO(),
        insulin_injection_method: "pump",
        timezone_utc_offset: DateUtils.tzOffset(from) / 60,
      },
    });
  };

  getPumpModeIndicators = (
    patientId: string,
    from: DateTime<true>,
    to: DateTime<true>,
  ): Promise<PumpModeIndicators | null> => {
    return this.datasource.get(`/practitioner/indicators/pump-mode/${patientId}/`, pumpModeIndicatorsSchema, {
      params: {
        start: from.startOf("day").toISO(),
        end: to.plus({ day: 1 }).startOf("day").toISO(),
      },
    });
  };

  getDexcomAuthorizeURL = () => {
    return this.datasource.get(
      "/patient/dexcom/authorize-url/",
      z.object({ url: cz.optional(z.string()), status: z.string() }),
    );
  };

  dexcomAuthCallback = (code: string) => {
    return this.datasource.post(`/patient/dexcom/callback/`, z.unknown(), {
      code,
    });
  };

  getDailyFileReport = (patientId: string, date: DateTime) => {
    return this.datasource.get(`/practitioner/daily-report/${patientId}/`, cz.noContent(dailyFileReportSchema), {
      params: {
        date: date.toISODate(),
      },
    });
  };
}
